/**
 * Frontend error tracking (CF-0522): captures uncaught errors and unhandled promise rejections, keeps a short
 * breadcrumb trail (route changes, API calls with request ids), and posts sanitized reports to
 * POST /api/client-errors/ where they are grouped by fingerprint. When VITE_SENTRY_DSN is set and the Sentry
 * browser SDK is present on window (loaded by the host page), reports also go there.
 */

type Crumb = { at: string; kind: string; message: string };
const crumbs: Crumb[] = [];
let installed = false;
let lastRequestId = "";
const RELEASE = import.meta.env.VITE_RELEASE ?? "";
const seen = new Map<string, number>();

export function breadcrumb(kind: string, message: string) {
  crumbs.push({ at: new Date().toISOString(), kind, message: message.slice(0, 200) });
  if (crumbs.length > 20) crumbs.shift();
}

export function noteRequestId(id: string | null) {
  if (id) lastRequestId = id;
}

async function report(message: string, stack: string) {
  const key = message.slice(0, 120);
  const n = (seen.get(key) ?? 0) + 1;
  seen.set(key, n);
  if (n > 5) return; // rate-limit per session
  try {
    await fetch("/api/client-errors/", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json", "X-Organization": localStorage.getItem("cf.organization") ?? "", "X-CSRFToken": document.cookie.match(/csrftoken=([^;]+)/)?.[1] ?? "" }, body: JSON.stringify({ message: message.slice(0, 500), stack: stack.slice(0, 8000), url: location.pathname, release: RELEASE, breadcrumbs: crumbs, request_id: lastRequestId }) });
  } catch {
    /* never throw from the reporter */
  }
  const sentry = (window as unknown as { Sentry?: { captureException: (e: unknown) => void } }).Sentry;
  if (sentry && import.meta.env.VITE_SENTRY_DSN) sentry.captureException(new Error(message));
}

export function installErrorTracking() {
  if (installed) return;
  installed = true;
  window.addEventListener("error", (ev) => { void report(String(ev.message ?? ev.error ?? "error"), String(ev.error?.stack ?? "")); });
  window.addEventListener("unhandledrejection", (ev) => { const r = ev.reason as { message?: string; stack?: string } | string; void report(typeof r === "string" ? r : String(r?.message ?? "unhandled rejection"), typeof r === "string" ? "" : String(r?.stack ?? "")); });
  const origFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const url = typeof input === "string" ? input : input instanceof URL ? input.toString() : input.url;
    const res = await origFetch(input, init);
    if (url.startsWith("/api/")) {
      noteRequestId(res.headers.get("X-Request-ID"));
      breadcrumb("api", `${init?.method ?? "GET"} ${url.split("?")[0]} → ${res.status}`);
    }
    return res;
  };
  breadcrumb("nav", location.pathname);
}
